import { useState, type FormEvent } from 'react';
import type { CreateVendorInput, Vendor } from '@dsr/shared-types';
import { formatPhoneDisplay } from '@dsr/shared-types';
import { PhoneInput } from '../components/PhoneInput';
import {
  useCreateVendor,
  useDeleteVendor,
  useLookup,
  useUpdateVendor,
  useVendors,
} from '../api/queries/financials';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Table,
  Textarea,
} from '../components/ui';

export function VendorsPage(): JSX.Element {
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [removing, setRemoving] = useState<Vendor | null>(null);

  const { data, isLoading, isError } = useVendors(q);
  const del = useDeleteVendor();
  const canWrite = can('vendor.write');
  const canDelete = can('vendor.delete');
  const vendors = data?.vendors ?? [];

  const onDelete = async () => {
    if (!removing) return;
    try {
      await del.mutateAsync(removing.id);
      toast.success('Vendor removed.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to remove vendor.');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="page vendors-page">
      <header className="page-header">
        <div>
          <h1>Vendors</h1>
          <p className="page-sub">Payee registry — who expenses get paid to.</p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New vendor
          </Button>
        )}
      </header>

      <div className="filters-bar">
        <form
          className="filter-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(qDraft);
          }}
        >
          <input
            type="search"
            className="form-input"
            placeholder="Search vendors…"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
          />
        </form>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {isError && <p className="error-text" role="alert">Failed to load vendors.</p>}
      {!isLoading && !isError && vendors.length === 0 && (
        <p className="muted">No vendors yet.</p>
      )}

      {vendors.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Location</Table.HeaderCell>
              <Table.HeaderCell>Contact</Table.HeaderCell>
              <Table.HeaderCell>Default tax category</Table.HeaderCell>
              {(canWrite || canDelete) && <Table.HeaderCell aria-label="Actions" />}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {vendors.map((v) => (
              <Table.Row key={v.id}>
                <Table.Cell tone="strong">
                  {v.name}
                  {v.dba_name && <div className="muted small">dba {v.dba_name}</div>}
                </Table.Cell>
                <Table.Cell>{[v.city, v.state].filter(Boolean).join(', ') || '—'}</Table.Cell>
                <Table.Cell>
                  {v.email ?? (v.phone ? formatPhoneDisplay(v.phone) : '—')}
                </Table.Cell>
                <Table.Cell>{v.default_tax_category ?? '—'}</Table.Cell>
                {(canWrite || canDelete) && (
                  <Table.Cell>
                    <div className="settings-actions">
                      {canWrite && (
                        <Button variant="ghost" onClick={() => setEditing(v)}>
                          Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" onClick={() => setRemoving(v)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && <VendorModal onClose={() => setCreateOpen(false)} />}
      {editing && <VendorModal vendor={editing} onClose={() => setEditing(null)} />}
      {removing && (
        <ConfirmDialog
          open
          title="Remove vendor"
          message={`Remove "${removing.name}"? Existing expenses keep their snapshot of the name.`}
          confirmLabel="Remove"
          danger
          busy={del.isPending}
          onConfirm={() => void onDelete()}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

function VendorModal({ vendor, onClose }: { vendor?: Vendor; onClose: () => void }): JSX.Element {
  const isEdit = vendor !== undefined;
  const [name, setName] = useState(vendor?.name ?? '');
  const [dba, setDba] = useState(vendor?.dba_name ?? '');
  const [city, setCity] = useState(vendor?.city ?? '');
  const [state, setState] = useState(vendor?.state ?? '');
  const [phone, setPhone] = useState(vendor?.phone ? formatPhoneDisplay(vendor.phone) : '');
  const [email, setEmail] = useState(vendor?.email ?? '');
  const [ein, setEin] = useState(vendor?.ein ?? '');
  const [taxCategory, setTaxCategory] = useState(vendor?.default_tax_category ?? '');
  const [notes, setNotes] = useState(vendor?.notes ?? '');

  const { data: taxCats } = useLookup('tax_category');
  const create = useCreateVendor();
  const update = useUpdateVendor();
  const busy = create.isPending || update.isPending;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await update.mutateAsync({
          id: vendor.id,
          name: name.trim(),
          dba_name: dba.trim() || null,
          city: city.trim() || null,
          state: state.trim() || null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          ein: ein.trim() || null,
          default_tax_category: taxCategory || null,
          notes: notes.trim() || null,
        });
        toast.success('Vendor updated.');
      } else {
        const input: CreateVendorInput = { name: name.trim() };
        if (dba.trim()) input.dba_name = dba.trim();
        if (city.trim()) input.city = city.trim();
        if (state.trim()) input.state = state.trim();
        if (phone.trim()) input.phone = phone.trim();
        if (email.trim()) input.email = email.trim();
        if (ein.trim()) input.ein = ein.trim();
        if (taxCategory) input.default_tax_category = taxCategory;
        if (notes.trim()) input.notes = notes.trim();
        await create.mutateAsync(input);
        toast.success('Vendor created.');
      }
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Save failed.');
    }
  };

  return (
    <Dialog
      title={isEdit ? `Edit ${vendor.name}` : 'New vendor'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="vendor-form"
            variant="primary"
            disabled={busy || name.trim().length === 0}
          >
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="vendor-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </FormField>
        <FormField label="DBA">
          <Input value={dba} onChange={(e) => setDba(e.target.value)} />
        </FormField>
        <FormField label="City">
          <Input value={city} onChange={(e) => setCity(e.target.value)} />
        </FormField>
        <FormField label="State">
          <Input value={state} onChange={(e) => setState(e.target.value)} />
        </FormField>
        <FormField label="Phone">
          <PhoneInput value={phone} onChange={setPhone} />
        </FormField>
        <FormField label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <FormField label="EIN (9 digits)">
          <Input
            value={ein}
            onChange={(e) => setEin(e.target.value)}
            placeholder="12-3456789"
          />
        </FormField>
        <FormField label="Default tax category">
          <select className="form-input" value={taxCategory} onChange={(e) => setTaxCategory(e.target.value)}>
            <option value="">None</option>
            {(taxCats?.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Notes" full>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </form>
    </Dialog>
  );
}
